import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api';
import usePolling from '../utils/usePolling';

// 5 min i synlig flik, paus i dold flik (A11, 2026-10-07). Listen är
// informativ; en synk pågår i regel långt mer än fem minuter.
const POLL_INTERVAL = 5 * 60 * 1000;

const bannerStyle = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  // Precis över appens footer (Layout.js, z-20), under allt med lager 22 eller
  // högre: modulernas bottenrader (25 sedan BL-429, andra 30-100), mobilmenyns
  // bakgrund (z-30), Tailwind-dialoger (z-40/z-50), KpPanel (300), rullgardiner
  // och modaler. Med 900 täckte banderollen knapparna längst ned i paneler och
  // dialoger (BL-423). Sidomenyn och några rader till rättas i BL-433.
  // Vanligt sidinnehåll får luft via padding nedan.
  zIndex: 21,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '10px 20px',
  background: 'var(--kp-accent-light, #EFF6FF)',
  borderTop: '1px solid var(--kp-accent, #3B82F6)',
  fontSize: '13px',
  fontWeight: 500,
  color: 'var(--kp-text, #1E293B)',
  animation: 'syncBannerIn 300ms ease forwards',
};

const dotStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: 'var(--kp-accent, #3B82F6)',
  animation: 'syncPulse 1.5s ease-in-out infinite',
  flexShrink: 0,
};

const dismissStyle = {
  marginLeft: 12,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--kp-text-faint, #8D9DB0)',
  fontSize: '12px',
  fontWeight: 600,
  padding: '4px 8px',
  borderRadius: 4,
};

export default function SyncBanner() {
  const [syncing, setSyncing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    try {
      const res = await apiFetch('/api/system/sync-status');
      if (res.ok) {
        const data = await res.json();
        setSyncing(data.syncing === true);
        if (data.syncing) setDismissed(false);
      }
      return res.ok;
    } catch {
      // silent — usePolling glesar ut vid fel
      return false;
    }
  }, []);

  usePolling(check, { intervalMs: POLL_INTERVAL });

  // Inject keyframes once
  useEffect(() => {
    if (!document.getElementById('sync-banner-keyframes')) {
      const s = document.createElement('style');
      s.id = 'sync-banner-keyframes';
      s.textContent = [
        '@keyframes syncBannerIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}',
        '@keyframes syncPulse{0%,100%{opacity:1}50%{opacity:.3}}',
      ].join('');
      document.head.appendChild(s);
    }
  }, []);

  // Medan banderollen syns får sidan lika mycket luft i nederkant, så att
  // knappar längst ned på sidan inte hamnar under den (BL-423).
  const bannerRef = useRef(null);
  const synlig = syncing && !dismissed;
  // useLayoutEffect: luften sätts och tas bort i samma uppdatering som banderollen.
  useLayoutEffect(() => {
    if (!synlig || !bannerRef.current) return undefined;
    const tidigare = document.body.style.paddingBottom;
    const satt = () => {
      if (bannerRef.current) document.body.style.paddingBottom = `${bannerRef.current.offsetHeight}px`;
    };
    satt();
    window.addEventListener('resize', satt); // texten kan radbrytas om vid ändrad bredd
    return () => {
      window.removeEventListener('resize', satt);
      if (tidigare) document.body.style.paddingBottom = tidigare;
      else document.body.style.removeProperty('padding-bottom');
    };
  }, [synlig]);

  if (!synlig) return null;

  return (
    <div ref={bannerRef} style={bannerStyle} role="status" aria-live="polite">
      <span style={dotStyle} />
      <span>Datasynkronisering pågår i bakgrunden. Applikationen kan vara långsammare än vanligt.</span>
      <button style={dismissStyle} onClick={() => setDismissed(true)} title="Dölj">
        Dölj
      </button>
    </div>
  );
}
